import { Box, Divider, TextField, Typography } from '@mui/material';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import GavelOutlinedIcon from '@mui/icons-material/GavelOutlined';
import NotesOutlinedIcon from '@mui/icons-material/NotesOutlined';
import React from 'react';
import { Controller } from 'react-hook-form';
import ReactQuill from 'react-quill';
import styled from 'styled-components';
import 'react-quill/dist/quill.snow.css';

const quillModules = {
    clipboard: { matchVisual: false },
    toolbar: [
        [{ header: [1, 2, 3, 4, 5, 6, false] }, { font: [] }],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ align: '' }, { align: 'center' }, { align: 'right' }, { align: 'justify' }],
        [{ color: [] }, { background: [] }],
        ['link'],
        ['clean'],
    ],
};

const quillFormats = [
    'header',
    'font',
    'list',
    'bullet',
    'bold',
    'italic',
    'underline',
    'strike',
    'align',
    'color',
    'background',
    'link',
];

const fieldStyles = {
    '& .MuiOutlinedInput-root': {
        borderRadius: '14px',
        background: '#fff',
        color: '#10285f',
        fontWeight: 700,

        '& fieldset': {
            borderColor: 'rgba(15, 34, 77, 0.16)',
        },

        '&:hover fieldset': {
            borderColor: 'rgba(214, 170, 58, 0.7)',
        },

        '&.Mui-focused fieldset': {
            borderColor: '#d6aa3a',
            borderWidth: '2px',
        },
    },

    '& .MuiInputLabel-root': {
        color: 'rgba(15, 34, 77, 0.65)',
        fontWeight: 700,
    },

    '& .MuiInputLabel-root.Mui-focused': {
        color: '#10285f',
    },
};

const Step3Content = ({ control, register, errors }) => {
    return (
        <Wrapper>
            <Header>
                <Typography className="title">
                    Contenido del contrato
                </Typography>

                <Typography className="subtitle">
                    Edita el título, descripción, términos y cláusulas que harán parte del documento final.
                </Typography>
            </Header>

            <SectionCard>
                <SectionTitle>
                    <ArticleOutlinedIcon />
                    <Box>
                        <Typography className="section-title">
                            Título del contrato
                        </Typography>
                        <Typography className="section-subtitle">
                            Nombre principal que identificará el documento
                        </Typography>
                    </Box>
                </SectionTitle>

                <TextField
                    fullWidth
                    size="small"
                    label="Título"
                    InputLabelProps={{ shrink: true }}
                    error={!!errors.title}
                    helperText={errors.title?.message}
                    sx={fieldStyles}
                    {...register('title', { required: 'Obligatorio.' })}
                />
            </SectionCard>

            <EditorSection>
                <SectionTitle>
                    <DescriptionOutlinedIcon />
                    <Box>
                        <Typography className="section-title">
                            Descripción
                        </Typography>
                        <Typography className="section-subtitle">
                            Presentación general del acuerdo entre las partes
                        </Typography>
                    </Box>
                </SectionTitle>

                <EditorBox $minHeight={220}>
                    <Controller
                        control={control}
                        name="description"
                        rules={{ required: 'La descripción es obligatoria.' }}
                        render={({ field }) => (
                            <ReactQuill
                                theme="snow"
                                value={field.value ?? ''}
                                onChange={field.onChange}
                                modules={quillModules}
                                formats={quillFormats}
                            />
                        )}
                    />
                </EditorBox>

                {errors.description && (
                    <Typography className="error-text">
                        {errors.description.message}
                    </Typography>
                )}
            </EditorSection>

            <EditorSection>
                <SectionTitle>
                    <GavelOutlinedIcon />
                    <Box>
                        <Typography className="section-title">
                            Términos y condiciones
                        </Typography>
                        <Typography className="section-subtitle">
                            Condiciones legales, obligaciones y reglas del contrato
                        </Typography>
                    </Box>
                </SectionTitle>

                <EditorBox $minHeight={320}>
                    <Controller
                        control={control}
                        name="terms_conditions"
                        rules={{ required: 'Los términos y condiciones son obligatorios.' }}
                        render={({ field }) => (
                            <ReactQuill
                                theme="snow"
                                value={field.value ?? ''}
                                onChange={field.onChange}
                                modules={quillModules}
                                formats={quillFormats}
                            />
                        )}
                    />
                </EditorBox>

                {errors.terms_conditions && (
                    <Typography className="error-text">
                        {errors.terms_conditions.message}
                    </Typography>
                )}
            </EditorSection>

            <EditorSection>
                <SectionTitle>
                    <NotesOutlinedIcon />
                    <Box>
                        <Typography className="section-title">
                            Cláusulas adicionales
                        </Typography>
                        <Typography className="section-subtitle">
                            Información complementaria u observaciones especiales
                        </Typography>
                    </Box>

                    <OptionalBadge>Opcional</OptionalBadge>
                </SectionTitle>

                <EditorBox $minHeight={180}>
                    <Controller
                        control={control}
                        name="special_clauses"
                        render={({ field }) => (
                            <ReactQuill
                                theme="snow"
                                value={field.value ?? ''}
                                onChange={field.onChange}
                                modules={quillModules}
                                formats={quillFormats}
                            />
                        )}
                    />
                </EditorBox>
            </EditorSection>
        </Wrapper>
    );
};

export default Step3Content;

const Wrapper = styled(Box)`
    width: 100%;
`;

const Header = styled.div`
    margin-bottom: 1rem;

    .title {
        color: #10285f;
        font-size: 1.25rem;
        font-weight: 900;
    }

    .subtitle {
        margin-top: 0.2rem;
        color: rgba(15, 34, 77, 0.62);
        font-size: 0.9rem;
        font-weight: 600;
    }
`;

const SectionCard = styled.div`
    padding: 1rem;
    margin-bottom: 1rem;
    border-radius: 18px;
    border: 1px solid rgba(15, 34, 77, 0.1);
    background: rgba(16, 40, 95, 0.025);
`;

const EditorSection = styled.div`
    padding: 1rem;
    margin-bottom: 1rem;
    border-radius: 18px;
    border: 1px solid rgba(15, 34, 77, 0.1);
    background: rgba(16, 40, 95, 0.025);

    .error-text {
        display: block;
        margin-top: 0.5rem;
        color: #d32f2f;
        font-size: 0.78rem;
        font-weight: 700;
    }
`;

const SectionTitle = styled.div`
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 1rem;

    svg {
        color: #d6aa3a;
        font-size: 1.45rem;
        flex-shrink: 0;
    }

    .section-title {
        color: #10285f;
        font-size: 1rem;
        font-weight: 900;
        line-height: 1.2;
    }

    .section-subtitle {
        color: rgba(15, 34, 77, 0.58);
        font-size: 0.8rem;
        font-weight: 600;
        margin-top: 0.1rem;
    }

    @media (max-width: 600px) {
        align-items: flex-start;
    }
`;

const OptionalBadge = styled.span`
    margin-left: auto;
    padding: 0.28rem 0.7rem;
    border-radius: 999px;
    background: rgba(214, 170, 58, 0.16);
    border: 1px solid rgba(214, 170, 58, 0.28);
    color: #10285f;
    font-size: 0.72rem;
    font-weight: 900;
    text-transform: uppercase;

    @media (max-width: 600px) {
        margin-left: 0;
    }
`;

const EditorBox = styled.div`
    background: #fff;
    border-radius: 16px;
    border: 1px solid rgba(15, 34, 77, 0.12);
    overflow: hidden;

    .ql-toolbar {
        border: none;
        border-bottom: 1px solid rgba(15, 34, 77, 0.1);
        background: linear-gradient(180deg, #ffffff 0%, #f8faff 100%);
    }

    .ql-container {
        border: none;
        min-height: ${({ $minHeight }) => `${$minHeight}px`};
        font-size: 0.95rem;
        color: #10285f;
        font-family: inherit;
    }

    .ql-editor {
        min-height: ${({ $minHeight }) => `${$minHeight}px`};
        padding: 1rem;
    }

    .ql-editor.ql-blank::before {
        color: rgba(15, 34, 77, 0.38);
        font-style: normal;
    }

    .ql-stroke {
        stroke: #10285f;
    }

    .ql-fill {
        fill: #10285f;
    }

    .ql-picker {
        color: #10285f;
    }

    &:focus-within {
        border-color: #d6aa3a;
        box-shadow: 0 0 0 1px rgba(214, 170, 58, 0.28);
    }

    @media (max-width: 600px) {
        .ql-container,
        .ql-editor {
            min-height: ${({ $minHeight }) => `${Math.max($minHeight - 80, 140)}px`};
        }
    }
`;